import type { FlightEvent, FlightSummary } from '@trajectory/astro';
import { create } from 'zustand';
import type { TelemetrySeries } from '@/lib/telemetry-series';

export type TelemetryStatus = 'idle' | 'running' | 'ready' | 'error';

interface TelemetryState {
  status: TelemetryStatus;
  /**
   * Not read reactively — charts pull `series` out via `getState()` inside
   * their own animation-frame loop. It lives in the store only so `status`
   * transitions and `series` updates stay atomic.
   */
  series: TelemetrySeries | null;
  events: FlightEvent[];
  summary: FlightSummary | null;
  error: string | null;
  setRunning: () => void;
  setReady: (series: TelemetrySeries, events: FlightEvent[], summary: FlightSummary) => void;
  setError: (message: string) => void;
  reset: () => void;
}

export const useTelemetryStore = create<TelemetryState>((set) => ({
  status: 'idle',
  series: null,
  events: [],
  summary: null,
  error: null,
  setRunning: () => set({ status: 'running', error: null }),
  setReady: (series, events, summary) => set({ status: 'ready', series, events, summary, error: null }),
  setError: (error) => set({ status: 'error', error }),
  reset: () => set({ status: 'idle', series: null, events: [], summary: null, error: null }),
}));
